import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import * as styles from './PageLayout.module.scss';
import circles from '../assets/images/circles/circlelines_greybluish.png';
import Layout from './Layout';
import MenuButton from './MenuButton';

const MINIMUM_AVAILABLE_HEIGHT_TO_SHOW_SIDE_CIRCLES = 600;

interface PageLayoutProps {
  sideBarChildren?: React.ReactNode;
  searchBox?: React.ReactNode;
  title: string;
  children: React.ReactNode;
  withPageContentFullWidth?: boolean;
  withPageContentBox?: boolean;
  hasFilters?: boolean;
  withoutCircles?: boolean;
  extraSectionChildren?: React.ReactNode;
}

const PageLayout = ({
  sideBarChildren,
  searchBox,
  title,
  children,
  withPageContentFullWidth = false,
  withPageContentBox,
  hasFilters = false,
  withoutCircles = false,
  extraSectionChildren,
}: PageLayoutProps): React.ReactElement => {
  const { t } = useTranslation();
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [isSideCirclesVisible, setIsSideCirclesVisible] = useState(false);

  const containerBottomLeftRef = useRef<HTMLDivElement | null>(null);
  const containerBottomRightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerBottomLeftRef.current && containerBottomRightRef.current) {
      setIsSideCirclesVisible(
        containerBottomRightRef.current.offsetHeight - containerBottomLeftRef.current.offsetHeight >
          MINIMUM_AVAILABLE_HEIGHT_TO_SHOW_SIDE_CIRCLES,
      );
    }
  }, [Math.random()]);

  return (
    <Layout>
      <div className={styles.circlesBackgroundContainer}>
        {!withoutCircles && <img className={styles.circlesBackground} src={circles} alt="" />}
      </div>
      <div className={styles.section + (withPageContentBox ? ' ' + styles.withPageContentBox : '')}>
        <div className={styles.container}>
          <div className={styles.containerTop}>
            <div className={searchBox ? styles.containerTopLeft : styles.containerTopLeftFullWidth}>
              <h1 className={styles.title}>{title}</h1>
            </div>
            {searchBox ? (
              <div className={styles.containerTopRight}>
                {searchBox}
                {hasFilters && (
                  <div className={styles.filtersButton}>
                    <MenuButton
                      label={t('filters.mobile_label', 'Filter')}
                      icon="ellipsis"
                      isMenuOpen={areFiltersOpen}
                      onTouch={() => {
                        setAreFiltersOpen(!areFiltersOpen);
                      }}
                    ></MenuButton>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.mockContainerTopRight}>
                <div className={styles.mockSearchBox}></div>
              </div>
            )}
          </div>
          <div className={styles.containerBottom}>
            {withPageContentFullWidth ? null : (
              <div className={styles.containerBottomLeftWindow}>
                <div
                  ref={containerBottomLeftRef}
                  className={
                    styles.containerBottomLeft + (areFiltersOpen ? ' ' + styles.menuOpen : '')
                  }
                >
                  {sideBarChildren}
                </div>
                <div className={isSideCirclesVisible ? ' ' + styles.withSideCircles : ''}></div>
              </div>
            )}
            <div
              ref={containerBottomRightRef}
              className={
                withPageContentFullWidth
                  ? styles.containerBottomFullWidth
                  : styles.containerBottomRight
              }
            >
              {children}
            </div>
          </div>
        </div>
      </div>
      {extraSectionChildren}
    </Layout>
  );
};

export default PageLayout;
